import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import DataTable from '../../components/admin/DataTable';
import { Search, Filter } from 'lucide-react';
import { format } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 30 };
      if (search) params.search = search;
      if (filterType) params.user_type = filterType;
      if (filterStatus) params.status = filterStatus;

      const response = await adminAPI.getUsers(params);
      setUsers(response.data || []);
      setTotalPages(response.meta?.totalPages || 1);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoading(false);
    }
  }, [page, search, filterType, filterStatus]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers();
  };

  const columns = [
    {
      header: 'User',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 bg-navy-200 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
            {row.profile_photo_url ? (
              <img
                src={row.profile_photo_url.startsWith('http') ? row.profile_photo_url : `${UPLOADS_BASE_URL}${row.profile_photo_url}`}
                alt="" className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-navy-600 text-xs font-medium">{row.first_name?.[0]}{row.last_name?.[0]}</span>
            )}
          </div>
          <div>
            <p className="text-sm font-medium text-navy-900">{row.first_name} {row.last_name}</p>
            <p className="text-xs text-navy-500">{row.email}</p>
          </div>
        </div>
      ),
    },
    {
      header: 'Type',
      render: (row) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
          row.user_type === 'helper' ? 'bg-purple-100 text-purple-700' :
          row.user_type === 'admin' ? 'bg-red-100 text-red-700' :
          'bg-primary-100 text-primary-700'
        }`}>
          {row.user_type?.replace('_', ' ')}
        </span>
      ),
    },
    {
      header: 'Status',
      render: (row) => (
        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
          row.status === 'active' ? 'bg-green-100 text-green-700' :
          row.status === 'suspended' ? 'bg-red-100 text-red-700' :
          'bg-yellow-100 text-yellow-700'
        }`}>
          {row.status}
        </span>
      ),
    },
    {
      header: 'Verification',
      render: (row) => {
        if (row.user_type !== 'helper') return <span className="text-navy-400">-</span>;
        const colors = { approved: 'text-green-600', pending: 'text-yellow-600', rejected: 'text-red-600' };
        return <span className={`text-xs font-medium ${colors[row.verification_status] || 'text-navy-400'}`}>{row.verification_status || '-'}</span>;
      },
    },
    {
      header: 'Activity',
      render: (row) => (
        <div className="text-xs text-navy-500">
          {row.user_type === 'yacht_owner' ? `${row.request_count || 0} requests` :
           row.user_type === 'helper' ? `${row.total_jobs || 0} jobs` : '-'}
        </div>
      ),
    },
    {
      header: 'Joined',
      render: (row) => (
        <span className="text-xs text-navy-500">
          {row.created_at ? format(new Date(row.created_at), 'MMM d, yyyy') : '-'}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-display font-semibold text-navy-900">User Management</h1>
        <p className="text-navy-500 mt-1">View and manage all registered users.</p>
      </div>

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-navy-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="input-field pl-10 w-full"
          />
        </form>
        <select
          value={filterType}
          onChange={(e) => { setFilterType(e.target.value); setPage(1); }}
          className="input-field w-auto"
        >
          <option value="">All Types</option>
          <option value="yacht_owner">Yacht Owners</option>
          <option value="helper">Helpers</option>
          <option value="admin">Admins</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
          className="input-field w-auto"
        >
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={users}
        loading={loading}
        emptyMessage="No users found"
        onRowClick={(row) => navigate(`/admin/users/${row.id}`)}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
}
